import React, { useCallback, useMemo, type ReactNode } from 'react';

import { useQueryClient } from '@lumapps/base-api/react-query';
import { contentQueryKeys } from '@lumapps/contents/api/contents';
import { CONTRIBUTION_MODES } from '@lumapps/contents/constants';
import {
    getCurrentContent,
    getCurrentContentPublicationStatus,
    getCurrentContributionMode,
    isNewWriterEditorEnabled,
} from '@lumapps/contents/ducks/selectors';
import type { Content, WriteModeContent } from '@lumapps/contents/types';
import { LegacyContentSettingsDialogVariant } from '@lumapps/designer/types';
import { ConfirmDialog } from '@lumapps/lumx-dialogs/components/ConfirmDialog';
import { usePromiseConfirmDialog } from '@lumapps/lumx-dialogs/hooks/usePromiseConfirmDialog';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { useRouter } from '@lumapps/router';
import { useTranslate } from '@lumapps/translations';
import { actions as localeActions } from '@lumapps/translations/ducks/slice';

import { CONTENT_EDITOR } from '../keys';
import { designerContentEdition } from '../routes';
import type { ContentSave } from '../types';
import { ContentEditorContext, type ContentEditorContextValue } from './ContentEditorContext';

export interface LegacyContentEditorProviderProps {
    updateContributionMode: (contributionMode: CONTRIBUTION_MODES, shouldStore?: boolean) => void;
    onSettingsOpen: (variant?: LegacyContentSettingsDialogVariant) => void;
    saveContent: ContentSave;
    updateLegacyContent: (
        params: { content: Content; isPartial?: false } | { content: Partial<Content>; isPartial: true },
    ) => void;
    publishContent: () => Promise<Content>;
    /**
     * Callback to force legacy input language sync.
     * It will broadcast an event that is needed for some widgets to refresh correctly.
     */
    onInputLanguageChange?: (lang?: string) => void;
    children: ReactNode;
}

export const LegacyContentEditorProvider: React.FC<LegacyContentEditorProviderProps> = ({
    children,
    updateContributionMode,
    onSettingsOpen,
    updateLegacyContent,
    saveContent,
    onInputLanguageChange,
    publishContent,
}) => {
    const { translateKey } = useTranslate();
    const contributionMode = useSelector(getCurrentContributionMode)!;
    const content = useSelector(getCurrentContent) as WriteModeContent;
    const publicationInformations = useSelector(getCurrentContentPublicationStatus);
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const handleSave = useCallback<ContentSave>(
        async (options) => {
            const content = await saveContent(options);
            queryClient.invalidateQueries({ queryKey: contentQueryKeys.getLiveRevision(content.id!) });
            return content;
        },
        [queryClient, saveContent],
    );
    const { open: openConfirmDialog, props: confirmDialogProps } = usePromiseConfirmDialog();

    const isWriterEditorEnabled = useSelector(isNewWriterEditorEnabled);
    const { redirect } = useRouter();

    const updateContributionModeWithConfirmation = useCallback(
        async (newMode: CONTRIBUTION_MODES, shouldStore = true) => {
            if (newMode === CONTRIBUTION_MODES.WRITER && isWriterEditorEnabled) {
                // When switching to writer mode with the new writer editor enabled,
                // we show a confirmation dialog to inform the user about the change.
                const confirmed = await openConfirmDialog({
                    id: 'confirm-switch-writer-mode',
                    title: translateKey(CONTENT_EDITOR.CONFIRM_SWITCH_WRITER_MODE_TITLE),
                    confirmMessage: translateKey(CONTENT_EDITOR.CONFIRM_SWITCH_CONTRIBUTION_MODE_MESSAGE),
                });
                if (confirmed) {
                    const savedContent = await handleSave({
                        checkSlug: true,
                        checkTitle: true,
                        shouldBlockLegacyRedirections: true,
                    });
                    redirect(designerContentEdition(savedContent.id!));
                }
            } else {
                updateContributionMode(newMode, shouldStore);
            }
        },
        [handleSave, isWriterEditorEnabled, openConfirmDialog, redirect, translateKey, updateContributionMode],
    );

    const value: ContentEditorContextValue = useMemo(
        () => ({
            state: {
                contributionMode,
                content,
            },
            publicationInformations,
            contentEditorActions: {
                publishContent,
                saveContent: handleSave,
                updateContributionMode: updateContributionModeWithConfirmation,
                onInputLanguageChange: (lang: string) => {
                    dispatch(localeActions.updateLocaleInputLanguage({ lang }));
                    onInputLanguageChange?.(lang);
                },
                openContentSettings: () => onSettingsOpen(LegacyContentSettingsDialogVariant.settings),
                updateContent: updateLegacyContent,
            },
        }),
        [
            content,
            contributionMode,
            dispatch,
            handleSave,
            onInputLanguageChange,
            onSettingsOpen,
            publicationInformations,
            publishContent,
            updateContributionModeWithConfirmation,
            updateLegacyContent,
        ],
    );

    return (
        <ContentEditorContext.Provider value={value}>
            {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
            {children}
        </ContentEditorContext.Provider>
    );
};
